import { useLingui } from '@lingui/react/macro'
import { PlusIcon } from '@radix-ui/react-icons'
import { Button } from 'designSystem/components/Button'
import DropdownSearch from 'features/navbar/DropdownSearch'
import { DrawerState } from 'features/rightDrawer'
import React, { useCallback, useState } from 'react'
import {
  createOrganizationSearch,
  editOrganizationSearch,
} from 'routes/organization/-searchParams'
import {
  createSiteSearch,
  editSiteSearch,
} from 'routes/organization_/site/-searchParams'
import styled from 'styled-components'
import { GeckoOrganization, GeckoSite, RolodexEntryType } from 'types'

import { SiteSelectorsProps } from './siteSelectorsTypes'
import { useSiteSelectorsState } from './useSiteSelectorsState'

const SiteSelectorsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.125rem;
`

const SelectorWrapper = styled.div`
  max-width: clamp(10rem, 15rem, 20rem);
  min-width: 8rem;
  overflow: hidden;
  flex: 0 1 auto;
`

export const StackedSiteSelectors = styled.div`
  width: 100%;

  ${SiteSelectorsContainer} {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }

  ${SelectorWrapper} {
    width: 100%;
    max-width: none;
  }
`

const StyledLinkDiv = styled.div`
  display: flex;
  justify-content: space-between;
`

type SelectorId = 'organization' | 'site'

const StyledButtonDiv = styled.div`
  display: flex;
  justify-content: end;
`

export const SiteSelectorsTopBar: React.FC<SiteSelectorsProps> = ({
  organizations,
  selectedOrganization,
  sites,
  selectedSite,
  isLoading,
  hasEverLoaded,
  hideSiteSelector = false,
}) => {
  const { t } = useLingui()
  const [openSelector, setOpenSelector] = useState<SelectorId | null>(null)
  const selectorOpenChange = (selector: SelectorId) => (open: boolean) =>
    setOpenSelector((current) => {
      if (open) return selector
      return current === selector ? null : current
    })

  const {
    navigate,
    setOrgAndSiteIds,
    setDrawerState,
    setDrawerEntry,
    orgsWithAssociatedSites,
    sitesFilteredByOrganization,
    handleSiteSelect,
    handleOrgSelect,
    canEditOrganizations,
    canEditSites,
  } = useSiteSelectorsState({
    organizations,
    selectedOrganization,
    selectedSite,
    sites,
  })

  const getEditLink = useCallback(
    (
      classToEdit: GeckoOrganization | GeckoSite,
      closeMenu: () => Promise<void>
    ) => {
      let linkTitle

      const handleEditClick = async (e: React.MouseEvent) => {
        e.stopPropagation()
        e.preventDefault()

        await closeMenu()
        setDrawerEntry(classToEdit)

        if (classToEdit instanceof GeckoOrganization) {
          await setOrgAndSiteIds({
            organizationId: classToEdit.id,
            siteId: undefined,
          })

          navigate({
            to: '/organization',
            search: editOrganizationSearch(classToEdit.id),
          })
        } else if (classToEdit instanceof GeckoSite) {
          await setOrgAndSiteIds({
            organizationId: classToEdit.tags[RolodexEntryType.ORGANIZATION],
            siteId: classToEdit.id,
          })

          navigate({
            to: '/organization/site',
            search: editSiteSearch(
              classToEdit.tags.organization,
              classToEdit.id
            ),
          })
        } else {
          setDrawerState(DrawerState.ErrorState)
        }
      }

      if (classToEdit instanceof GeckoOrganization) {
        linkTitle = t`Edit Org`
      } else if (classToEdit instanceof GeckoSite) {
        linkTitle = t`Edit Site`
      } else {
        linkTitle = t`Edit`
      }

      return (
        <div onClick={handleEditClick}>
          <a data-edit-link='true'>{linkTitle}</a>
        </div>
      )
    },
    [setDrawerEntry, setOrgAndSiteIds, navigate, setDrawerState, t]
  )

  const getAddButton = useCallback(
    (classToAdd: DrawerState, closeMenu: () => Promise<void>) => {
      let title
      if (classToAdd === DrawerState.EditGeckoOrganization) {
        title = t`Add Org`
        return (
          <StyledButtonDiv>
            <Button
              variant='secondary'
              onClick={async (e) => {
                await closeMenu()
                await setOrgAndSiteIds({
                  organizationId: undefined,
                  siteId: undefined,
                })
                navigate({
                  to: '/organization',
                  search: createOrganizationSearch(),
                })
                e.preventDefault()
              }}>
              <PlusIcon />
              {title}
            </Button>
          </StyledButtonDiv>
        )
      } else if (classToAdd === DrawerState.EditGeckoSite) {
        title = t`Add Site`
        return (
          <StyledButtonDiv>
            <Button
              variant='secondary'
              onClick={async (e) => {
                await closeMenu()
                await setOrgAndSiteIds((prev) => ({
                  organizationId: prev.organizationId,
                  siteId: undefined,
                }))
                if (!selectedOrganization?.id) {
                  e.preventDefault()
                  return
                }
                navigate({
                  to: '/organization/site',
                  search: createSiteSearch(selectedOrganization.id),
                })
                e.preventDefault()
              }}>
              <PlusIcon />
              {title}
            </Button>
          </StyledButtonDiv>
        )
      } else {
        throw new Error(
          `Unexpected getAddButton state! ${DrawerState[classToAdd]}`
        )
      }
    },
    [setOrgAndSiteIds, navigate, selectedOrganization?.id, t]
  )

  return (
    <SiteSelectorsContainer>
      <SelectorWrapper>
        <DropdownSearch
          open={openSelector === 'organization'}
          onOpenChange={selectorOpenChange('organization')}>
          <DropdownSearch.Trigger
            contentData={selectedOrganization}
            isSingleItem={orgsWithAssociatedSites.length === 1}
            isLoading={(isLoading || !hasEverLoaded) && !selectedOrganization}
            testId='org-dropdown'
          />
          <DropdownSearch.Menu>
            <DropdownSearch.FilteredSearch
              listToFilter={orgsWithAssociatedSites}
              itemGetter={({ organization }, closeMenu) => (
                <StyledLinkDiv>
                  {organization.data.name}
                  {canEditOrganizations
                    ? getEditLink(organization, closeMenu)
                    : null}
                </StyledLinkDiv>
              )}
              itemSearchTerms={({ organization }) => [
                organization.data.name,
                organization.data.name.replaceAll('.', ''),
                organization.data.name.replace(/[^A-Z]+/g, ''), // Capital letters only
              ]}
              onSelect={handleOrgSelect}
              placeholder={t`Search Organizations(…)`}
              extra={({ closeMenu }) =>
                canEditOrganizations
                  ? getAddButton(DrawerState.EditGeckoOrganization, closeMenu)
                  : null
              }
            />
          </DropdownSearch.Menu>
        </DropdownSearch>
      </SelectorWrapper>

      {!hideSiteSelector && (
        <SelectorWrapper>
          <DropdownSearch
            open={openSelector === 'site'}
            onOpenChange={selectorOpenChange('site')}>
            <DropdownSearch.Trigger
              contentData={selectedSite}
              isSingleItem={sitesFilteredByOrganization.length === 1}
              isLoading={(isLoading || !hasEverLoaded) && !selectedSite}
            />
            <DropdownSearch.Menu>
              <DropdownSearch.FilteredSearch
                listToFilter={sitesFilteredByOrganization}
                itemGetter={(site, closeMenu) => (
                  <StyledLinkDiv>
                    {site.data.name}
                    {canEditSites ? getEditLink(site, closeMenu) : null}
                  </StyledLinkDiv>
                )}
                itemSearchTerms={(site) => [
                  site.data.name,
                  site.data.name.replaceAll('.', ''),
                  site.data.name.replace(/[^A-Z]+/g, ''), // Capital letters only
                ]}
                onSelect={handleSiteSelect}
                placeholder={t`Search Sites(…)`}
                extra={({ closeMenu }) =>
                  canEditSites
                    ? getAddButton(DrawerState.EditGeckoSite, closeMenu)
                    : null
                }
              />
            </DropdownSearch.Menu>
          </DropdownSearch>
        </SelectorWrapper>
      )}
    </SiteSelectorsContainer>
  )
}

export const SiteSelectors = SiteSelectorsTopBar
